// src/pages/ProfileGate/steps/FinancialSetupStep.jsx

import React, {
  useState,
} from "react";

import {
  useNavigate,
} from "react-router-dom";

import {
  OnboardingFormCard,
  OnboardingInterstitial,
} from "../OnboardingVisuals";

import {
  ANNUAL_REVENUE_OPTIONS_V3,
  BUSINESS_BANKING_OPTIONS,
  FINANCE_TRACKING_OPTIONS_V3,
  useProfileGateDraft,
} from "../../../hooks/useProfileGateDraft";

import {
  saveOnboardingDraftProgress,
} from "../../../lib/onboarding/profileGate";

const MONEY_PENGUIN_URL =
  "https://kwbgrjakobarqelunebd.supabase.co/storage/v1/object/public/pictures/MVP4/Onboarding/Gwin_Book.png";

function SelectField({
  label,
  value,
  options,
  placeholder,
  onChange,
}) {
  return (
    <label className="block">
      <span className="mb-2 block text-[14px] font-semibold leading-snug text-[#333333] sm:text-[15px]">
        {label}
      </span>

      <div className="relative">
        <select
          value={
            value || ""
          }
          onChange={(event) =>
            onChange(
              event.target.value
            )
          }
          className={[
            "w-full appearance-none rounded-[16px] border-2 bg-white",
            "px-4 py-3.5 pr-11",
            "text-[15px] font-medium text-[#333333]",
            "outline-none transition",
            "border-[#E1E1E1]",
            "focus:border-[#FF914D]",
          ].join(" ")}
        >
          <option value="">
            {placeholder}
          </option>

          {options.map(
            (option) => {
              const item =
                typeof option ===
                "string"
                  ? {
                      label:
                        option,
                      value:
                        option,
                    }
                  : option;

              return (
                <option
                  key={
                    item.value
                  }
                  value={
                    item.value
                  }
                >
                  {
                    item.label
                  }
                </option>
              );
            }
          )}
        </select>

        <div
          className="
            pointer-events-none
            absolute
            right-4
            top-1/2
            -translate-y-1/2
            text-[#757575]
          "
        >
          <svg
            width="18"
            height="18"
            viewBox="0 0 20 20"
            fill="none"
            aria-hidden="true"
          >
            <path
              d="M5 7.5L10 12.5L15 7.5"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </div>
      </div>
    </label>
  );
}

export default function FinancialSetupStep() {
  const navigate =
    useNavigate();

  const {
    loading,
    draft,
    setField,
    mergeFields,
  } =
    useProfileGateDraft();

  const [
    saving,
    setSaving,
  ] = useState(false);

  const [
    error,
    setError,
  ] = useState("");

  if (loading) {
    return null;
  }

  const annualRevenue =
    draft.annual_revenue_range ||
    "";

  const financeTrackingMethod =
    draft.finance_tracking_method ||
    "";

  const businessBankingType =
    draft.business_banking_type ||
    "";

  const canContinue =
    Boolean(
      annualRevenue &&
        financeTrackingMethod &&
        businessBankingType
    );

  const handleBankingSelect = (
    option
  ) => {
    setError("");

    let legacyBoolean = null;

    if (
      option ===
      "I use a separate account for the business"
    ) {
      legacyBoolean = true;
    }

    if (
      option ===
      "I use an account that also has personal money going through it"
    ) {
      legacyBoolean = false;
    }

    mergeFields({
      business_banking_type:
        option,

      bank_accounts_separate:
        legacyBoolean,
    });
  };

  const handleContinue =
    async () => {
      if (
        !canContinue ||
        saving
      ) {
        return;
      }

      try {
        setSaving(true);
        setError("");

        await saveOnboardingDraftProgress(
          {
            ...draft,

            annual_revenue_range:
              annualRevenue,

            finance_tracking_method:
              financeTrackingMethod,

            business_banking_type:
              businessBankingType,
          },
          {
            resumeStep:
              "summary",
          }
        );

        navigate(
          "/profile-gate/summary"
        );
      } catch (err) {
        console.error(
          "Could not save financial setup answers:",
          err
        );

        setError(
          err?.message ||
            "Could not save your answers. Please try again."
        );
      } finally {
        setSaving(false);
      }
    };

  return (
    <OnboardingInterstitial
      message="How are you managing your business money today?"
      secondaryMessage=""
      showBack={true}

      showSpeechPenguin={true}
      speechPenguinUrl={
        MONEY_PENGUIN_URL
      }
      speechPenguinClassName="
        w-[105px]
        sm:w-[125px]
        md:w-[140px]
      "

      nextLabel={
        saving
          ? "Saving..."
          : "Continue"
      }
      nextDisabled={
        !canContinue ||
        saving
      }
      onBack={() =>
        navigate(
          "/profile-gate/business-basics"
        )
      }
      onNext={
        handleContinue
      }
    >
      <div className="mx-auto mt-7 w-full max-w-[560px]">
        <div className="space-y-4">
          <OnboardingFormCard>
            <SelectField
              label="Roughly what is your annual revenue?"
              value={
                annualRevenue
              }
              options={
                ANNUAL_REVENUE_OPTIONS_V3
              }
              placeholder="Select annual revenue"
              onChange={(
                value
              ) => {
                setError("");

                setField(
                  "annual_revenue_range",
                  value
                );
              }}
            />
          </OnboardingFormCard>

          <OnboardingFormCard>
            <SelectField
              label="What do you mainly use to keep track of the money?"
              value={
                financeTrackingMethod
              }
              options={
                FINANCE_TRACKING_OPTIONS_V3
              }
              placeholder="Select an option"
              onChange={(
                value
              ) => {
                setError("");

                setField(
                  "finance_tracking_method",
                  value
                );
              }}
            />
          </OnboardingFormCard>

          <OnboardingFormCard>
            <SelectField
              label="Which best describes your business banking?"
              value={
                businessBankingType
              }
              options={
                BUSINESS_BANKING_OPTIONS
              }
              placeholder="Select an option"
              onChange={
                handleBankingSelect
              }
            />
          </OnboardingFormCard>
        </div>

        {error ? (
          <p className="mt-4 text-center text-sm font-medium text-red-600">
            {error}
          </p>
        ) : null}
      </div>
    </OnboardingInterstitial>
  );
}